/**
 * configAccessors.js — Tenant Config Read API
 *
 * PURPOSE:
 *   Single source of truth for reading tenant config from components.
 *   Replaces scattered `getState().tenant.theme.X || "fallback"` reads
 *   that drift across files.
 *
 *   Pair with normalize.js: normalize.js writes the canonical shape into
 *   state; these accessors read it back with safe fallbacks.
 *
 * USAGE:
 *   import { getTheme, getUiFeature } from "../../core/configAccessors.js";
 *   const label = getTheme("button_text", "Ask Concierge");
 *   const showMic = getUiFeature("show_mic", true);
 *
 * SEE: widget/CONFIG_CONTRACT.md for canonical keys.
 */

import { getState } from "./state.js";

function tenant() {
  return getState().tenant || {};
}

function isMissing(v) {
  return v === undefined || v === null || v === "";
}

export function getTheme(key, fallback = null) {
  const v = tenant().theme?.[key];
  return isMissing(v) ? fallback : v;
}

export function getContact(key, fallback = null) {
  const v = tenant().contact?.[key];
  return isMissing(v) ? fallback : v;
}

// Commerce7-specific integration settings (currently just the per-tenant
// Stripe publishable key used to tokenize a NEW card for club-signup payment —
// see features/optional/stripe_c7.js). Publishable keys are meant to be
// public/client-side, so keeping this readable from tenant config (which
// ships to the widget bundle) doesn't violate the "secrets only on backend"
// rule — no Stripe secret key is ever involved in this flow.
export function getCommerce7Config(key, fallback = null) {
  const v = tenant().commerce7?.[key];
  return isMissing(v) ? fallback : v;
}

export function getUiFeature(key, defaultBool = true) {
  const v = tenant().ui_features?.[key];
  if (typeof v === "boolean") return v;
  if (isMissing(v)) return defaultBool;
  return Boolean(v);
}

export function getFeature(name, defaultBool = false) {
  const v = tenant().features?.[name];
  if (typeof v === "boolean") return v;
  if (isMissing(v)) return defaultBool;
  return Boolean(v);
}

export function getGuidedFlow() {
  const v = tenant().guided_flow_questions;
  return Array.isArray(v) ? v : [];
}

export function getTenantId() {
  return tenant().tenant_id || null;
}

// Host-page search bar config, or null when this winery has none. Shape is
// guaranteed by normalize.js (mount.selector present, suggestions an array),
// so callers only need the null check. See CONFIG_CONTRACT.md `search_bar`.
export function getSearchBar() {
  const v = tenant().search_bar;
  return v && typeof v === "object" ? v : null;
}

// TEMPORARY hardcoded gate for the Commerce7 wine-club membership feature
// (join club / wine selection / club checkout / manage membership). Not yet
// backed by a tenant-config flag — per explicit instruction, a plain constant
// until this is wired to real tenant config (would be tenant.features.membership).
// Membership itself is generic/common (features/optional/club_c7.js works for
// any Commerce7 tenant); this list is just which tenants have it turned on.
const MEMBERSHIP_TENANTS = new Set(["valledellapace"]);

export function isMembershipEnabled() {
  return MEMBERSHIP_TENANTS.has(getTenantId());
}

export function getDisplayName(fallback = "") {
  const v = tenant().display_name;
  return isMissing(v) ? fallback : v;
}
